// eslint-disable-next-line no-restricted-imports
import { useMutation } from "@tanstack/react-query";
import axios, { AxiosError, AxiosResponse } from "axios";
import { EmailCategoryPreference, UpdateEmailPreferencesRequest } from "types/generated/models";
import { useSnackbar } from "@powerdiary/components-library/lib";
import { showNetworkErrorSnackbar } from "utils/networkErrorSnackbar";
import { emailPreferencesUrl } from "./useEmailPreferences";

export interface IUseUpdateEmailPreferencesHook {
    mutate: (preferences: EmailCategoryPreference[]) => void;
    isPending: boolean;
    isSuccess: boolean;
    // The client's email changed since the link was sent.
    isNoLongerValid: boolean;
    isNotFound: boolean;
}

// Anonymous page outside the portal shell, so it cannot use usePostMutation (see frontend-client-portal.md).
export function useUpdateEmailPreferences(token: string): IUseUpdateEmailPreferencesHook {
    const { enqueueSnackbar } = useSnackbar();
    const { mutate, isPending, isSuccess, error } = useMutation<
        AxiosResponse<void>,
        AxiosError,
        EmailCategoryPreference[]
    >({
        mutationFn: (preferences) =>
            axios.post<void>(emailPreferencesUrl(token), {
                preferences,
            } satisfies UpdateEmailPreferencesRequest),
        onError: (mutationError) => {
            const status = mutationError.response?.status;
            if (status === 404 || status === 409) {
                return;
            }

            if (mutationError.request && !mutationError.response) {
                showNetworkErrorSnackbar();
            } else {
                enqueueSnackbar("Please try again later.", {
                    variant: "error",
                    alertTitle: "Your email preferences were not updated",
                });
            }
        },
    });

    return {
        mutate,
        isPending,
        isSuccess,
        isNoLongerValid: error?.response?.status === 409,
        isNotFound: error?.response?.status === 404,
    };
}
